import { useState, useEffect, useMemo } from "react";
import { useSnapshot } from "valtio";
import { createUseStyles } from "react-jss";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import EventSelector from "../EventSelector";
import ImageListImage from "../ImageListImage";

import { fetchApi } from "../../services/api";
import { currentAccount } from "../../proxies";

/**
 * ScenesPanel — the left-hand panel of TouchTour Studio (WP-C).
 *
 * It replaces the old floating ImageList in the layout's left slot. It owns the
 * scene library: it fetches the captured scenes for the selected event, then
 * lets the user filter (All / Placed / Unplaced), search by name and sort the
 * list before dragging a scene onto the board.
 *
 * Dragging still uses the existing react-dnd "new-image" source from
 * ImageListImage, so dropping a scene on the canvas calls onAddImage exactly as
 * before — this panel only changes how the list is presented, not how scenes
 * are added.
 */

const PANEL_WIDTH = 260;

const TABS = {
  all: "all",
  placed: "placed",
  unplaced: "unplaced",
};

const SORTS = {
  capture: "capture",
  name: "name",
  placedFirst: "placedFirst",
};

const SORT_OPTIONS = [
  { value: SORTS.capture, label: "Capture Order" },
  { value: SORTS.name, label: "Name" },
  { value: SORTS.placedFirst, label: "Placed first" },
];

const ScenesPanel = ({ images: placedImages, onAddImage, tourInfo }) => {
  const classes = useStyles();
  const { event } = useSnapshot(currentAccount);
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(TABS.all);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(SORTS.capture);

  // Same /images/list/{event} fetch the old ImageList used: scenes follow the
  // selected event from the EventSelector below.
  useEffect(() => {
    if (event === null) {
      setScenes([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    fetchApi(`/images/list/${event}`).then(items => {
      setScenes(
        items.map(item => ({
          id: item.id.toString(),
          name: item.title,
          width: item.width ?? null,
          height: item.height ?? null,
          fov: item.fov ?? null,
          x: 0,
          y: 0,
          urls: {
            full: item.url,
            low: item.url_low,
          },
          cube: item.cube ?? null,
          hotspots: [],
        })),
      );
      setLoading(false);
    });
  }, [event]);

  // A scene is "placed" when its id already exists in the board image map.
  const placedIds = useMemo(
    () => new Set(Object.keys(placedImages ?? {})),
    [placedImages],
  );
  const isPlaced = scene => placedIds.has(scene.id);

  // Scenes already on the board may have been placed from a different event
  // than the one currently selected, so the /images/list fetch won't include
  // them. Merge them in (deduped by id) so every placed scene is always listed.
  const allScenes = useMemo(() => {
    const byId = new Map();
    scenes.forEach(scene => byId.set(scene.id, scene));
    Object.values(placedImages ?? {}).forEach(image => {
      const id = image.id?.toString();
      if (id && !byId.has(id)) {
        byId.set(id, { ...image, id });
      }
    });
    return [...byId.values()];
  }, [scenes, placedImages]);

  const placedCount = useMemo(
    () => allScenes.filter(isPlaced).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allScenes, placedIds],
  );
  const unplacedCount = allScenes.length - placedCount;

  const visibleScenes = useMemo(() => {
    let list = allScenes;

    if (tab === TABS.placed) {
      list = list.filter(isPlaced);
    } else if (tab === TABS.unplaced) {
      list = list.filter(scene => !isPlaced(scene));
    }

    const query = search.trim().toLowerCase();
    if (query.length > 0) {
      list = list.filter(scene => scene.name.toLowerCase().includes(query));
    }

    if (sort === SORTS.name) {
      list = [...list].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true }),
      );
    } else if (sort === SORTS.placedFirst) {
      // Stable: placed scenes first, capture order preserved within each group.
      list = [...list].sort(
        (a, b) => Number(isPlaced(b)) - Number(isPlaced(a)),
      );
    }

    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allScenes, tab, search, sort, placedIds]);

  const renderTab = (key, label, count) => (
    <button
      type="button"
      className={clsx(classes.tab, { [classes.tabActive]: tab === key })}
      onClick={() => setTab(key)}
    >
      {label}
      {count !== undefined && <span className={classes.tabCount}>{count}</span>}
    </button>
  );

  return (
    <div className={classes.container}>
      <EventSelector className={classes.selector} tourInfo={tourInfo} light />

      <div className={classes.header}>Scenes</div>

      <div className={classes.tabs}>
        {renderTab(TABS.all, "All", allScenes.length)}
        {renderTab(TABS.placed, "Placed", placedCount)}
        {renderTab(TABS.unplaced, "Unplaced", unplacedCount)}
      </div>

      <div className={classes.controls}>
        <div className={classes.searchWrap}>
          <FontAwesomeIcon
            icon={["fal", "magnifying-glass"]}
            className={classes.searchIcon}
            fixedWidth
          />
          <input
            type="text"
            className={classes.search}
            placeholder="Find scene…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select
          className={classes.sort}
          value={sort}
          onChange={e => setSort(e.target.value)}
          title="Sort scenes"
        >
          {SORT_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className={classes.images}>
        {loading && <div className={classes.empty}>Loading scenes…</div>}
        {!loading && visibleScenes.length === 0 && (
          <div className={classes.empty}>
            {allScenes.length === 0 ? "No scenes in this event." : "No matches."}
          </div>
        )}
        {!loading &&
          visibleScenes.map(scene => (
            <ImageListImage
              key={scene.id}
              image={scene}
              onAddImage={onAddImage}
              disabled={isPlaced(scene)}
            />
          ))}
      </div>
    </div>
  );
};

const useStyles = createUseStyles(theme => ({
  container: {
    flex: `0 0 ${PANEL_WIDTH}px`,
    width: PANEL_WIDTH,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.colorBackground,
    borderRight: `1px solid ${theme.colorDarkGrey}`,
    boxSizing: "border-box",
    overflow: "hidden",
  },
  selector: {
    padding: [theme.spacing2, theme.spacing2, 0],
  },
  header: {
    color: theme.colorGrey,
    fontSize: 11,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 1,
    padding: [theme.spacing2, theme.spacing2, 0],
  },
  tabs: {
    display: "flex",
    gap: theme.spacing1,
    padding: [theme.spacing2, theme.spacing2, 0],
  },
  tab: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flex: "1 1 0",
    justifyContent: "center",
    padding: [6, 8],
    fontSize: 12,
    color: theme.colorLightGrey,
    backgroundColor: "transparent",
    border: `1px solid ${theme.colorDarkGrey}`,
    borderRadius: theme.borderRadius,
    cursor: "pointer",
    whiteSpace: "nowrap",
    transitionProperty: "color, border-color, background-color",
    transitionDuration: theme.transitionDuration,
    "&:hover": {
      borderColor: theme.colorPrimary,
    },
  },
  tabActive: {
    color: theme.textColor,
    borderColor: theme.colorPrimary,
    backgroundColor: theme.colorPrimary,
  },
  tabCount: {
    fontVariantNumeric: "tabular-nums",
    opacity: 0.8,
  },
  controls: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing1,
    padding: [theme.spacing2, theme.spacing2, 0],
  },
  searchWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  searchIcon: {
    position: "absolute",
    left: 8,
    fontSize: 12,
    color: theme.colorGrey,
    pointerEvents: "none",
  },
  search: {
    width: "100%",
    boxSizing: "border-box",
    padding: [7, 8, 7, 26],
    fontSize: 13,
    color: theme.textColor,
    backgroundColor: theme.colorBlack,
    border: `1px solid ${theme.colorDarkGrey}`,
    borderRadius: theme.borderRadius,
    outline: "none",
    "&:focus": {
      borderColor: theme.colorPrimary,
    },
    "&::placeholder": {
      color: theme.colorGrey,
    },
  },
  sort: {
    width: "100%",
    boxSizing: "border-box",
    padding: [7, 8],
    fontSize: 13,
    color: theme.textColor,
    backgroundColor: theme.colorBlack,
    border: `1px solid ${theme.colorDarkGrey}`,
    borderRadius: theme.borderRadius,
    outline: "none",
    cursor: "pointer",
    "&:focus": {
      borderColor: theme.colorPrimary,
    },
  },
  images: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing2,
  },
  empty: {
    color: theme.colorGrey,
    fontSize: 12,
    textAlign: "center",
    padding: theme.spacing3,
  },
}));

export default ScenesPanel;
